import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import PageLayout from '@BUP/components/Layout/Page'
import TitleDashboard from '@BUP/components/Generics/TitleDashboard'
import SubtitleDashboard from '@BUP/components/Generics/SubtitleDashboard'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import PeriodDashboard from '@BUP/components/CourseAssignment/PeriodDashboard'
import CsvUploadCard from '@BUP/components/CourseAssignment/CsvUploadCard'
import { useGetCurrentPeriodStatus } from '@BUP/hooks/CourseAssignment/useGetCurrentPeriodStatus'
import { useGetAvailableGrades } from '@BUP/hooks/CourseAssignment/useGetAvailableGrades'
import { useOpenPeriod } from '@BUP/hooks/CourseAssignment/useOpenPeriod'
import { useClosePeriod } from '@BUP/hooks/CourseAssignment/useClosePeriod'
import { useGetRoster } from '@BUP/hooks/CourseAssignment/useGetRoster'
import { useGetExclusions } from '@BUP/hooks/CourseAssignment/useGetExclusions'
import { useUploadExclusions } from '@BUP/hooks/CourseAssignment/useUploadExclusions'
import { useGetEvaluations } from '@BUP/hooks/CourseAssignment/useGetEvaluations'
import { useUploadEvaluations } from '@BUP/hooks/CourseAssignment/useUploadEvaluations'
import { useDownloadLegacyExport } from '@BUP/hooks/CourseAssignment/useDownloadLegacyExport'
import { useSociogramGenderExport } from '@BUP/hooks/CourseAssignment/useSociogramGenderExport'
import { buildCsv, buildLegacyCsv, downloadCsv } from '@BUP/utils/csv'

// Not offered to schools yet - exclusions/evaluations upload, the course-generator export, and the
// drag-and-drop results view are hidden until they're ready to roll out. Flip this back on when
// they are; nothing else needs to change.
const SHOW_UNRELEASED_SECTIONS = false

// Fallback only, matching the backend's own default (see MAX_PREFERENCES in
// services/course-assignment.js) - the real limit is Organization.courseAssignmentMaxPreferences.
const DEFAULT_MAX_SELECTION = 5

const CourseAssignmentPeriodDashboardPage = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const navigate = useNavigate()
  const organization = useContext(OrganizationContext)
  const maxSelection = organization?.courseAssignmentMaxPreferences ?? DEFAULT_MAX_SELECTION

  const { grades, isLoading: isLoadingGrades } = useGetAvailableGrades()
  // A period scopes exactly one grade, and different grades can each have their own open at the
  // same time (see the backend's openPeriod doc comment) - so "the current period" only makes
  // sense once a grade is picked. Default to the first available one so a single-grade school
  // still sees its dashboard with zero clicks; a multi-grade school can switch freely.
  const [selectedGrade, setSelectedGrade] = useState(null)
  useEffect(() => {
    if (!selectedGrade && grades.length) setSelectedGrade(grades[0])
  }, [grades, selectedGrade])

  const { isLoading, error, period, refetch } = useGetCurrentPeriodStatus(selectedGrade)
  const { mutateAsync: openPeriod, isPending: isOpening } = useOpenPeriod()
  const { mutateAsync: closePeriod, isPending: isClosing } = useClosePeriod()
  const { roster } = useGetRoster(period?.id)
  const {
    isLoading: isLoadingExclusions,
    exclusions
  } = useGetExclusions(period?.id)
  const { mutateAsync: uploadExclusions, isPending: isUploadingExclusions } = useUploadExclusions()
  const {
    isLoading: isLoadingEvaluations,
    evaluations
  } = useGetEvaluations(period?.id)
  const { mutateAsync: uploadEvaluations, isPending: isUploadingEvaluations } = useUploadEvaluations()
  const { mutateAsync: downloadLegacyExport, isPending: isExporting } = useDownloadLegacyExport()
  const {
    isLoading: isLoadingGender,
    hasSurvey: hasGenderSurvey,
    hasKnownTemplate: hasKnownGenderTemplate,
    getGenderFor
  } = useSociogramGenderExport(roster)

  const handleOpen = useCallback(async ({ grade, allowOtherCourses }) => {
    await openPeriod({ grade, allowOtherCourses })
    refetch()
  }, [openPeriod, refetch])

  const handleClose = useCallback(async (periodId) => {
    await closePeriod(periodId)
    refetch()
  }, [closePeriod, refetch])

  const handleDownloadRoster = useCallback(() => {
    const csv = buildCsv(['enrollmentId', 'nombre'], roster.map(student => ({
      enrollmentId: student.enrollmentId,
      nombre: student.name
    })))
    downloadCsv('roster-alumnos.csv', csv)
  }, [roster])

  const handleDownloadGender = useCallback(() => {
    const csv = buildLegacyCsv(['Estudiante', 'Curso', 'Genero'], roster.map(student => ({
      Estudiante: student.name,
      Curso: student.courseName,
      Genero: getGenderFor(student.enrollmentId)
    })))
    downloadCsv('04 Estudiantes genero.csv', csv)
  }, [roster, getGenderFor])

  return (
    <PageLayout>
      <>
        <TitleDashboard icon={UserIcon} text={t('title')} />
        <p>{t('intro', { max: maxSelection })}</p>
        <PeriodDashboard
          isLoading={isLoading}
          error={error}
          period={period}
          grades={grades}
          isLoadingGrades={isLoadingGrades}
          selectedGrade={selectedGrade}
          onSelectGrade={setSelectedGrade}
          onOpen={handleOpen}
          onClose={handleClose}
          isOpening={isOpening}
          isClosing={isClosing}
        />
        {period && (
          <>
            <SubtitleDashboard icon={UserIcon} text={t('preferencesSectionTitle')} />
            <p>{t('preferencesSectionDescription')}</p>
            <div style={{ marginBottom: '1rem' }}>
              <ButtonComponent
                buttonText={t('viewResponsesButton')}
                colorType='primary'
                onClick={() => navigate(`/course-assignment/responses?grade=${encodeURIComponent(selectedGrade)}`)}
              />
            </div>

            {SHOW_UNRELEASED_SECTIONS && (
              <>
                <SubtitleDashboard icon={UserIcon} text={t('exclusionsEvaluationsSectionTitle')} />
                <div style={{ marginBottom: '1rem' }}>
                  <ButtonComponent
                    buttonText={t('downloadRosterButton')}
                    colorType='tertiary'
                    size='small'
                    onClick={handleDownloadRoster}
                    disabled={!roster.length}
                  />
                </div>
                <CsvUploadCard
                  title={t('exclusionsTitle')}
                  description={t('exclusionsDescription')}
                  onUpload={(file) => uploadExclusions({ periodId: period.id, file })}
                  isUploading={isUploadingExclusions}
                  currentCount={exclusions.length}
                  isLoadingCurrent={isLoadingExclusions}
                />
                <CsvUploadCard
                  title={t('evaluationsTitle')}
                  description={t('evaluationsDescription')}
                  onUpload={(file) => uploadEvaluations({ periodId: period.id, file })}
                  isUploading={isUploadingEvaluations}
                  currentCount={evaluations.length}
                  isLoadingCurrent={isLoadingEvaluations}
                />

                <SubtitleDashboard icon={UserIcon} text={t('exportSectionTitle')} />
                <p>{t('exportDescription')}</p>
                <div style={{ marginBottom: '1rem' }}>
                  <ButtonComponent
                    buttonText={t('exportButton')}
                    colorType='primary'
                    onClick={() => downloadLegacyExport(period.id)}
                    disabled={isExporting}
                  />
                </div>
                <p>
                  {isLoadingGender
                    ? t('loadingStatus')
                    : !hasGenderSurvey
                        ? t('exportGenderNoSurvey')
                        : !hasKnownGenderTemplate
                            ? t('exportGenderUnknownTemplate')
                            : t('exportGenderAvailable')}
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  <ButtonComponent
                    buttonText={t('exportGenderButton')}
                    colorType='tertiary'
                    size='small'
                    onClick={handleDownloadGender}
                    disabled={!roster.length || isLoadingGender}
                  />
                </div>

                <SubtitleDashboard icon={UserIcon} text={t('resultsSectionTitle')} />
                <p>{t('resultsSectionDescription')}</p>
                <div style={{ marginBottom: '1rem' }}>
                  <ButtonComponent
                    buttonText={t('viewResultsButton')}
                    colorType='primary'
                    onClick={() => navigate(`/course-assignment/results?grade=${encodeURIComponent(selectedGrade)}`)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </>
    </PageLayout>
  )
}

export default CourseAssignmentPeriodDashboardPage
